import { type BaseQueryApi, type FetchArgs, retry } from '@reduxjs/toolkit/query';
import { auth0BaseQuery } from '@store/auth0BaseQuery';

import type { QueueViewKey } from '../types/encounterWork';
import { QUEUE_RUN_PAGE_LIMIT, type QueueRunPage, type QueueRunPageError } from '../utils/queueRun';

import { queueRunBackoff } from './queueRunBackoff';

/**
 * One page of a queue run, read through a base query that tries again.
 *
 * The run walks up to ten pages behind the biller, and one page lost to a
 * blip would end it early with a rail that quietly stopped short of the
 * queue. So a page that fails for a reason worth retrying is read again —
 * three more times, with backoff — before the run records the failure. The
 * billing APIs themselves stay on the plain `auth0BaseQuery`: a card read
 * that fails should say so at once, not after several seconds of retrying.
 */

/** How many times a page is read again after it fails. */
export const QUEUE_RUN_PAGE_RETRIES = 3;

/**
 * Whether a failed page is worth reading again.
 *
 * A network failure, a timeout or a 5xx is the transport or the server
 * having a moment. A 4xx is the server's answer, and asking again gets the
 * same one. `auth0BaseQuery` folds transport failures into a numeric 500,
 * so the string statuses here are for a base query that does not.
 */
export function isTransientPageError(error: unknown): boolean {
  if (!error || typeof error !== 'object' || !('status' in error)) return false;
  const { status } = error;
  if (typeof status === 'number') return status >= 500;
  return status === 'FETCH_ERROR' || status === 'TIMEOUT_ERROR';
}

const retryingBaseQuery = retry(auth0BaseQuery, {
  backoff: queueRunBackoff,
  retryCondition: (error, _args, { attempt }) =>
    attempt <= QUEUE_RUN_PAGE_RETRIES && isTransientPageError(error),
});

/**
 * The claim-work page read a run makes: one queue, oldest first, a hundred
 * rows, hidden rows excluded — the read the card makes, at the run's size.
 */
export function queueRunPageArgs(queue: QueueViewKey, cursor: string | null): FetchArgs {
  return {
    params: {
      /* `undefined`, never null, for the first page: a null would be written
         onto the URL as the word "null". */
      cursor: cursor ?? undefined,
      include_hidden: false,
      limit: QUEUE_RUN_PAGE_LIMIT,
      sort: 'service_date_asc',
      work_view: queue,
    },
    url: '/v2/provider/billing/v2/claim-work',
  };
}

export type QueueRunPageResult =
  | { data: QueueRunPage; error?: undefined }
  | { data?: undefined; error: QueueRunPageError };

/** `retry` throws rather than returns when the read is aborted mid-loop. */
function thrownPageError(thrown: unknown): QueueRunPageError {
  const handled = thrown as { value?: { error?: QueueRunPageError } } | null;
  if (handled?.value?.error) return handled.value.error;
  return {
    error: thrown instanceof Error ? thrown.message : String(thrown),
    status: 'CUSTOM_ERROR',
  };
}

/** Reads one page; `null` asks for the first. Never throws. */
export async function readQueueRunPage(
  queue: QueueViewKey,
  cursor: string | null,
  api: BaseQueryApi,
): Promise<QueueRunPageResult> {
  try {
    const result = await retryingBaseQuery(queueRunPageArgs(queue, cursor), api, {});
    return result.error ? { error: result.error } : { data: result.data as QueueRunPage };
  } catch (thrown) {
    return { error: thrownPageError(thrown) };
  }
}
